import { notification } from 'antd';
import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, UseFormHandleSubmit, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { unstable_serialize, useSWRConfig } from 'swr';
import { InternalConfiguration } from 'swr/_internal';
import { CreateTicketFormValues } from '../../../types/form';
import useFormName from '../../hooks/ticketDetailPage/useFormName';
import useTicketFieldDefinitionList, {
    getUIFriendlyTicketFieldDefinitionList
} from '../../hooks/ticketDetailPage/useTicketFieldDefinitionList';
import {
    CreateTicketPostData,
    CreateTicketReturnedData,
    FieldDefinitionMap,
    FieldRuleOnForm,
    IFieldDefinition,
    TICKET_FORM_FIELDS_KEY,
    TicketSystemFieldsKey,
    TicketSystemFieldsKeyMap,
    actionAfterSubmit,
    channelTypeMap
} from '../../model/types';
import { createTicket } from '../../services/tickets/tickets';
import { useMacroStore, useTabStore, useTicketFormUtilStore } from '../../store/RootStoreContext';
import { getSelectedTabKeyFromUrl } from '../../store/childStore/tabStore';
import {
    BasicFormMethodsContextInterface,
    BasicFormMethodsProvider
} from '../../store/provider/BasicFormMethodsProvider';
import {
    controlledUserFormValue,
    parseSerializedValueFromUserFormValue,
    parseUserIdFromSerializedValue
} from '../../utils/forms/formHelper.parser';
import { nonNullishValue } from '../../utils/helpers';
import requestWithMessage from '../../utils/requestWithMessage';
import { pushTo } from '../../utils/routeHelper';
import {
    StorageHelperKeys,
    getLocalStorageItem,
    getSessionStorageItem,
    removeSessionStorageItem
} from '../../utils/storageHelpers';
import { filterSWRKeys } from '../../utils/swr';
import { customFieldTypeMap } from '../CustomField/CustomField.type';
import { fireResetRichTextEditorStateEvent } from '../GrispiRichTextEditor/plugins/ResetEditorStatePlugin';
import { getMentionedUsersFromComment } from '../TicketPage/MentionedUserController';
import FormPersistController from './FormPersistController';

interface Props {
    defaultValues: CreateTicketFormValues;
}

export const hasFieldUserValueType = (fieldDefinition: IFieldDefinition) =>
    [customFieldTypeMap.USER, customFieldTypeMap.MULTI_USER, customFieldTypeMap.UNTRUSTED_END_USER].includes(
        fieldDefinition.type as 'USER' | 'MULTI_USER' | 'UNTRUSTED_END_USER'
    );

const getCreateTicketPostDataFromFormValues = (
    formValues: CreateTicketFormValues,
    fieldListMap: FieldDefinitionMap,
    { cache, session }: { cache: InternalConfiguration['cache']; session: Session }
): CreateTicketPostData => {
    const {
        comment,
        currentAttachments: { inlineImages, attachments },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        formName,
        fields,
        ...rest
    } = formValues;

    const currentActiveFieldListCacheKey = unstable_serialize([
        `/forms/${fields[TicketSystemFieldsKey.formId.replace(new RegExp('\\.', 'gi'), '$')]}`,
        session.userInfo.username,
        session.tenantId
    ]);
    const currentActiveFieldList = (cache.get(currentActiveFieldListCacheKey)?.data?.fields as FieldRuleOnForm[])?.map(
        (field) => field.key
    );

    const postDataFields: CreateTicketPostData['fields'] = [];
    Object.entries(fields).forEach(([key, value]) => {
        const postKey = key.replaceAll('$', '.');

        if (currentActiveFieldList.includes(postKey) && value) {
            const postValue =
                typeof value === 'string'
                    ? hasFieldUserValueType(fieldListMap[postKey])
                        ? controlledUserFormValue(value)
                        : value
                    : value;

            postDataFields.push({
                key: postKey,
                value: postValue
            });
        }
    });

    const { body } = comment;
    const filteredInlineImages = inlineImages.filter(({ src }) => body?.includes(src)).map(({ id }) => id);
    const attachmentIds = [...filteredInlineImages, ...attachments.map((v) => v.id)];

    const mentionedUsers = getMentionedUsersFromComment(comment.body)
        .map(parseSerializedValueFromUserFormValue)
        .map(parseUserIdFromSerializedValue);

    return {
        ...rest,
        comment: {
            ...comment,
            channel: channelTypeMap.WEB,
            mentionedUsers,
            // optional prop for attachments if any
            ...(attachmentIds.length > 0 && {
                attachmentIds
            })
        },
        fields: postDataFields
    };
};

const CreateTicketForm = ({ children, defaultValues }: PropsWithChildren<Props>) => {
    const { t: tTickets } = useTranslation('tickets');
    const { t: tError } = useTranslation('error');

    const { cache, mutate } = useSWRConfig();
    const { data: session } = useSession();

    const { asPath } = useRouter();
    const formName = useFormName();
    const { data: allFields } = useTicketFieldDefinitionList(null, { revalidateOnMount: true });
    const [formHydrated, setFormHydrated] = useState(getSessionStorageItem(formName) ? false : true);
    const [api, notificationOnSubmitError] = notification.useNotification();

    const { resetFileList } = useTicketFormUtilStore();
    const { closeTabAndGetNewTargetUrl } = useTabStore();
    const { initNewTicketPlaceholderValues } = useMacroStore();

    const methods = useForm<CreateTicketFormValues>({
        mode: 'onChange',
        defaultValues,
        shouldFocusError: true
    });

    const { getValues, handleSubmit, reset, setValue, trigger } = methods;

    useEffect(() => {
        const persistedFormValues = getSessionStorageItem(formName);

        if (persistedFormValues && !formHydrated) {
            Object.entries(persistedFormValues).forEach(([key, value]) => {
                if (nonNullishValue(value) && typeof value === 'object' && !Array.isArray(value)) {
                    Object.entries(value).forEach(([subKey, subValue]) => {
                        if (nonNullishValue(subValue) && typeof subValue === 'object' && !Array.isArray(subValue)) {
                            Object.entries(subValue).forEach(([subSubKey, subSubValue]) => {
                                setValue(`${key}.${subKey}.${subSubKey}` as any, subSubValue, { shouldDirty: true });
                            });
                            return;
                        }

                        setValue(`${key}.${subKey}` as any, subValue, { shouldDirty: true });
                    });
                }
                setValue(key as any, value, { shouldDirty: true });
            });
            // console.debug(`CreateTicketForm , hydrating persistedFormValues, formName:`, formName);
            setFormHydrated(true);
            trigger();
            return;
        }
        // console.debug(`CreateTicketForm , no reason to hydrate`, formName);
    }, [formHydrated, formName, setValue, trigger]);

    const onValid: SubmitHandler<CreateTicketFormValues> = useCallback(
        async (formValues) => {
            const postData = getCreateTicketPostDataFromFormValues(
                formValues,
                getUIFriendlyTicketFieldDefinitionList(allFields).listMap,
                { cache, session }
            );
            const startMessage = tTickets('message.onCreateStart');
            const finishMessage = tTickets('message.onCreateFinish');
            await requestWithMessage({
                key: 'create-ticket',
                startMessage,
                finishMessage,
                apiRequest: async () => {
                    const actionAfterSubmit: actionAfterSubmit = getLocalStorageItem(
                        StorageHelperKeys.ACTION_AFTER_SUBMIT
                    );

                    const returnedData: CreateTicketReturnedData = await createTicket(postData);

                    resetFileList(true);

                    const selectedTabKey = getSelectedTabKeyFromUrl(asPath);
                    if (actionAfterSubmit === 'stay') {
                        closeTabAndGetNewTargetUrl(selectedTabKey);

                        const { key } = returnedData;
                        await mutate(filterSWRKeys('/tickets/mine-recent'));
                        pushTo(`/tickets/${key}`);
                        return;
                    }
                    const targetUrl = closeTabAndGetNewTargetUrl(selectedTabKey);
                    await mutate(filterSWRKeys('/tickets/mine-recent'));
                    targetUrl && pushTo(targetUrl);
                }
            });
        },
        [allFields, asPath, cache, session, tTickets, resetFileList, closeTabAndGetNewTargetUrl, mutate]
    );

    const onSubmit = useCallback(
        (handleOnSubmit: ReturnType<UseFormHandleSubmit<CreateTicketFormValues>>) => (e?: React.BaseSyntheticEvent) =>
            handleOnSubmit(e).catch((error) => {
                if (error.name === 'AxiosError') {
                    // to convert isSubmitting value to false without any data lost
                    reset(getValues(), {
                        keepDirtyValues: true,
                        keepErrors: true,
                        keepDirty: true,
                        keepValues: true,
                        keepDefaultValues: true,
                        keepIsSubmitted: true,
                        keepTouched: true,
                        keepIsValid: true,
                        keepSubmitCount: true
                    });
                    return;
                }
                api.info({
                    message: tError('ticketSubmissionError.title'),
                    description: tError('ticketSubmissionError.message'),
                    duration: 0
                });
            }),
        [api, getValues, reset, tError]
    );

    // we're able to reach below methods via context easily
    const basicFormMethods: BasicFormMethodsContextInterface = useMemo(
        () => ({
            submit: () => {
                trigger().then((isValid) => {
                    !isValid &&
                        setValue(
                            `${TICKET_FORM_FIELDS_KEY}.${TicketSystemFieldsKeyMap.status}`,
                            defaultValues[TICKET_FORM_FIELDS_KEY][TicketSystemFieldsKeyMap.status],
                            { shouldValidate: true }
                        );
                    isValid && onSubmit(handleSubmit(onValid))();
                });
            },
            resetForm: (formName: string) => {
                removeSessionStorageItem(formName);
                resetFileList(true);
                fireResetRichTextEditorStateEvent({ eventListenerKey: formName });

                reset();
                // should re-init to be able to pick the new values
                initNewTicketPlaceholderValues();
            }
        }),
        [
            defaultValues,
            handleSubmit,
            initNewTicketPlaceholderValues,
            onSubmit,
            onValid,
            reset,
            resetFileList,
            setValue,
            trigger
        ]
    );

    return (
        <BasicFormMethodsProvider value={basicFormMethods}>
            <FormProvider {...methods}>
                <FormPersistController formName={formName} />
                {notificationOnSubmitError}
                <form className="full-height" onSubmit={onSubmit(handleSubmit(onValid))}>
                    {children}
                </form>
            </FormProvider>
        </BasicFormMethodsProvider>
    );
};
export default CreateTicketForm;
